<script setup lang="ts">
/**
 * Singbac — Incorporation Post Studio panel.
 * Live document package (7종) + 엔티티 자동바인딩(회사 필드).
 * Aura(incorporation-pre) 패널을 복제하되 Singbac 7종 문서·per-director 스코프에 맞춰 축약.
 */
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideCddFill, bareKey, typeOf, isProgressFilled } from '../incorporation-post/cdd/useCddFill';
import {
  provideSgPreSigners,
  SG_PRE_SIGNER_KEYS,
  buildSgPreSignersFromValues,
} from '../incorporation-post/cdd/sgPreSignersContext';
import SingbacIncorporationPostPackageDoc from '../incorporation-post/SingbacIncorporationPostPackageDoc.vue';
import { normalizeSingbacPreFormDataForWord } from '../utils/normalizeSingbacPreFormDataForWord';
import { mapSingbacEntityToPostValues } from '../mappers/mapSingbacEntityToPostValues';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import {
  getActiveDirectors,
  getActiveSecretaries,
  getActiveMembers,
} from '@/features/startTask/preset_task/sg/components/sgDocGenEntityApi';
import { entityApi } from '@/features/Entity/api/entityApi';
import { dateToHtmlInput } from '@/features/startTask/preset_task/sg/components/change-financial-year-end/composables/dateToHtmlInput';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import { isIndividualNominator, formatNominatorAddress } from '@/features/Entity/utils/nominatorFormat';
import {
  loadCmBaseCodeOptions,
  nationalityLabelForOutput,
} from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import '../incorporation-post/allotDoc.css';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: Record<string, any> | null;
  editTaskId?: number | null;
  studio?: boolean;
  pendingDocType?: string;
  appliedAddress?: {
    key: string;
    line: string;
    town: string;
    state: string;
    postcode: string;
    country: string;
    seq: number;
  } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const fill = provideCddFill();
const signersCtx = provideSgPreSigners();
const docRef = ref<InstanceType<typeof SingbacIncorporationPostPackageDoc> | null>(null);

const entityCd = computed(() =>
  String(
    props.entityData?.entityCd ||
      props.entityData?.icpCd ||
      props.chatFormData?.entityCd ||
      props.chatFormData?.icpCd ||
      '',
  ).trim(),
);

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => {
    taskTitleLocal.value = v;
  },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const studio = computed(() => !!props.studio);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => {
  presetProgress.busy = v;
  presetProgress.message = v ? progress.value || 'Creating task…' : '';
});
watch(progress, (m) => {
  if (creating.value) presetProgress.message = m;
});

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const lastAutofillIcp = ref('');
// Edit 재진입: 저장값 복원 후엔 회사 자동채움(비동기, 복원보다 늦게 끝남)이 노미니·이사 부수와 직접 고친 값을 덮지 않게 한다.
//   빈 칸만 채우고 나머지(노미니 판정·인물 스코프·부수)는 저장값 유지. 다른 회사로 바꾼 경우에만 새로 채운다.
const restoredFromSaved = ref(false);
const autofilledKeys = ref<Set<string>>(new Set());

// ─── Nominee Director (L_OFFICER.IS_NOMINEE=1) ───
//  노미니 디렉터가 있어야 "Notice by Nominee Director" 와 "Nominee Director Service &
//  Indemnity Agreement" 가 생성된다. 엔티티 이사 register 의 IS_NOMINEE=1 로 자동 판정하되,
//  register 미갱신 대비 오퍼레이터가 수동 토글로 덮어쓸 수 있다.
const hasNomineeDirector = ref(false);
const nomineeName = ref('');

function emitProgress() {
  const total = fill.totalFields.value || 1;
  const filled = fill.filledCount.value;
  const pct = Math.min(100, Math.round((filled / total) * 100));
  emit('progress-change', pct);
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

/**
 * 필드 키 → officer 조회 role.
 * 공통 SG Incorporation (Post) 패널(PreSetIncorporationPostPanel.lookupForKey)을 미러링:
 *  · 회사명            → 엔티티 재선택 (incorporation)
 *  · 이사(client)      → DIRECTOR (선택한 엔티티의 이사 목록)
 *  · 비서             → SECRETARY (엔티티 비서)
 *  · 주주/subscriber   → SHAREHOLDER (엔티티 멤버)
 *  · resident director / 증인 → CONTACT (Singbac 자체 인력 · 엔티티 아님)
 *  · 그 외 인물        → CONTACT (client-wide contacts)
 */
function lookupForKey(bare: string): {
  defaultRole: string;
  lookupType: string;
  memberEntityKind?: 'individual' | 'corporate';
  entityCd: string;
} {
  // 회사명 → L_ENTITY 재선택
  if (bare === 'company_name' || bare === 'entity_name') {
    return { defaultRole: '', lookupType: 'incorporation', entityCd: '' };
  }
  // Singbac 자체 Resident Director / 증인 — 선택한 client 엔티티의 officer 아님 → 일반 Contacts
  if (/resident_director/.test(bare) || /witness/.test(bare)) {
    return { defaultRole: 'CONTACT', lookupType: 'officer', entityCd: '' };
  }
  // Secretary → 엔티티 비서 register
  if (/secretary/.test(bare)) {
    return { defaultRole: 'SECRETARY', lookupType: 'officer', entityCd: entityCd.value || '' };
  }
  // DRIW subscriber / 멤버 → 엔티티 SHAREHOLDER(멤버)
  if (/member_name$/.test(bare)) {
    return {
      defaultRole: 'SHAREHOLDER',
      lookupType: 'officer',
      memberEntityKind: 'individual',
      entityCd: entityCd.value || '',
    };
  }
  // client 이사 / first director / director_name → 엔티티 DIRECTOR register
  if (bare === 'client_name' || bare === 'first_director_names' || /director_name/.test(bare)) {
    return { defaultRole: 'DIRECTOR', lookupType: 'officer', entityCd: entityCd.value || '' };
  }
  // 그 외 인물 필드 — client-wide contacts
  return { defaultRole: 'CONTACT', lookupType: 'officer', entityCd: '' };
}

fill.setActivateHandler((key: string) => {
  const bare = bareKey(key);
  const t = typeOf(key);
  if (t === 'address') {
    emit('request-address-input', key);
    return;
  }
  if (t !== 'entity' && t !== 'person') return;

  const spec = lookupForKey(bare);
  emit('request-person-lookup', {
    fieldGroup: 'singbac-incorporation-post',
    key: bare,
    scopedKey: key,
    defaultRole: spec.defaultRole || undefined,
    lookupType: spec.lookupType,
    entityCd: spec.entityCd || '',
    ...(spec.memberEntityKind ? { memberEntityKind: spec.memberEntityKind } : {}),
  });
});

fill.setAddressHandler((key: string) => {
  emit('request-address-input', key);
});

watch(
  () => props.appliedAddress,
  (a) => {
    if (!a?.key) return;
    const line = [a.line, [a.postcode, a.town].filter(Boolean).join(' '), a.state, a.country]
      .map((s) => String(s || '').trim())
      .filter(Boolean)
      .join(', ');
    if (line) fill.setValue(a.key, line);
  },
);

watch(
  () => props.selectedPerson,
  async (p) => {
    if (!p) return;
    let person: Record<string, any> = { ...(p as any) };
    const contactCd = String(person.contactCd || person.CONTACT_CD || '').trim();
    if (contactCd) {
      try {
        const raw: any = await contactsApi.getContactById(contactCd);
        const c: any = raw?.contact || raw?.data?.contact || raw?.data || raw || {};
        person = {
          ...c,
          ...person,
          BLOCK_NO: c.BLOCK_NO ?? c.blockNo ?? person.BLOCK_NO,
          STREET_NAME: c.STREET_NAME ?? c.streetName ?? person.STREET_NAME,
          BUILDING_NAME: c.BUILDING_NAME ?? c.buildingName ?? person.BUILDING_NAME,
          ADDR_LEVEL: c.ADDR_LEVEL ?? c.addrLevel ?? person.ADDR_LEVEL,
          ADDR_UNIT: c.ADDR_UNIT ?? c.addrUnit ?? person.ADDR_UNIT,
          POSTAL_CODE: c.POSTAL_CODE ?? c.postalCode ?? person.POSTAL_CODE,
          FOREIGN_ADDRESS_LINE_1:
            c.FOREIGN_ADDRESS_LINE_1 ?? c.foreignAddressLine1 ?? person.FOREIGN_ADDRESS_LINE_1,
          FOREIGN_ADDRESS_LINE_2:
            c.FOREIGN_ADDRESS_LINE_2 ?? c.foreignAddressLine2 ?? person.FOREIGN_ADDRESS_LINE_2,
          primaryAddressParts: c.primaryAddressParts ?? person.primaryAddressParts,
          alternateAddressParts: c.alternateAddressParts ?? person.alternateAddressParts,
          NATIONALITY_CD:
            c.NATIONALITY_CD ?? c.nationalityCd ?? person.NATIONALITY_CD ?? person.nationalityCd,
          nationality:
            c.NATIONALITY_CD ?? c.nationalityCd ?? c.nationality ?? person.NATIONALITY_CD ?? person.nationality,
          nationalityName:
            c.nationalityName ?? c.nationalityText ?? c.NATIONALITY_NAME ?? person.nationalityName,
        };
      } catch (err) {
        console.warn('[SingbacPost] contact enrich failed:', err);
      }
    }
    await fill.applyPersonToActive(person);
    emitProgress();
  },
);

/** entityData(선택한 설립 회사) → 회사 필드 자동 시드 (빈 칸만) */
function seedCompanyFromEntityData(ed: Record<string, any> | null) {
  if (!ed) return;
  const setIfEmpty = (key: string, val: unknown) => {
    const s = String(val ?? '').trim();
    if (s && !String(fill.values[key] || '').trim()) fill.setValue(key, s);
  };
  setIfEmpty('company_name', ed.entityName || ed.companyName || ed.company_name || ed.ENTITY_NM);
  setIfEmpty(
    'company_registration_no',
    ed.entityUen || ed.registrationNo || ed.regNo || ed.uen || ed.ENTITY_UEN,
  );
  setIfEmpty(
    'registered_office_address',
    ed.registeredAddress || ed.registeredOfficeAddress || ed.registered_office_address,
  );
}

/** 엔티티(e) → 등록주소 문자열 조립 (표준 entity 필드). */
function buildEntityAddress(e: Record<string, any>): string {
  const g = (...ks: string[]) => {
    for (const k of ks) { const v = String(e?.[k] ?? '').trim(); if (v) return v; }
    return '';
  };
  const block = g('BLOCK_NO', 'blockNo', 'blockHouseNo', 'entityBlockNo');
  const street = g('STREET_NAME', 'streetName', 'entityStreetName');
  const level = g('ADDR_LEVEL', 'addrLevel', 'level', 'entityAddrLevel');
  const unit = g('ADDR_UNIT', 'addrUnit', 'unit', 'entityAddrUnit');
  const bld = g('BUILDING_NAME', 'buildingName', 'buildingEstateName', 'entityBuildingName');
  const postal = g('POSTAL_CODE', 'postalCode', 'entityPostalCode');
  const unitStr = level && unit ? `#${level}-${unit}` : level ? `#${level}` : '';
  const parts = [
    [block, street].filter(Boolean).join(' '),
    unitStr,
    bld,
    postal ? `Singapore ${postal}` : '',
  ].filter(Boolean);
  return parts.join(', ');
}

// ── 노미니 디렉터 / nominator (Notice by Nominee Director) ──────────────────
//  엔티티 officer register(L_OFFICER)에서 IS_NOMINEE=1 이사를 판정하고, 그 이사의
//  NOMINEE_DIRCTOR_CD 가 가리키는 contact 로 Notice 의 개인 지명자(nominator) particulars(client_*)를 채운다.
const NOMINATOR_KEYS = [
  'client_name', 'client_aliases', 'client_address', 'client_nationality',
  'client_nric', 'client_dob',
];
// 노미니(resident) 이사 자신의 신원 — Indemnity 의 Nominee Director NRIC/주소 등.
const RESIDENT_DIRECTOR_KEYS = [
  'resident_director_name', 'resident_director_nric', 'resident_director_address',
  'resident_director_nationality', 'resident_director_dob', 'resident_director_aliases',
];

/** getEntityOfficer 응답 → active officer 배열(auto+manual). */
function extractOfficers(offRes: any): any[] {
  const d = offRes?.data ?? offRes ?? {};
  const auto = Array.isArray(d.autoOfficers) ? d.autoOfficers : [];
  const manual = Array.isArray(d.manualOfficers) ? d.manualOfficers : [];
  const isActive = (o: any) => {
    const del = o?.isDel ?? o?.IS_DEL;
    return del === undefined || del === null || del === '' || del === 0 || del === '0' || del === false;
  };
  return [...auto, ...manual].filter(isActive);
}
function isNomineeOfficer(o: any): boolean {
  const v = o?.isNominee ?? o?.IS_NOMINEE;
  return v === true || v === 1 || v === '1' || String(v).toUpperCase() === 'Y';
}
function nomineeCodeOf(o: any): string {
  return String(o?.nomineeDirectorCode || o?.NOMINEE_DIRCTOR_CD || o?.nominee_director_cd || '').trim();
}

/** contactCd → applyPersonToKey 가 기대하는 정규화 person 객체(비서/이사 ✨ enrich 와 동일 매핑). */
async function enrichContact(contactCd: string): Promise<Record<string, any> | null> {
  const cd = String(contactCd || '').trim();
  if (!cd) return null;
  try {
    const raw: any = await contactsApi.getContactById(cd);
    const c: any = raw?.contact || raw?.data?.contact || raw?.data || raw || {};
    return {
      ...c,
      name: c.name || c.contactName || c.CONTACT_NAME ||
        [c.FIRST_NAME, c.MIDDLE_NAME, c.SURNAME].filter(Boolean).join(' ') ||
        [c.firstName, c.middleName, c.surname].filter(Boolean).join(' '),
      contactRegNo: c.contactRegNo ?? c.CONTACT_REG_NO ?? c.nricNo ?? c.passportNo,
      dateOfBirth: c.dateOfBirth ?? c.DATE_OF_BIRTH,
      BLOCK_NO: c.BLOCK_NO ?? c.blockNo,
      STREET_NAME: c.STREET_NAME ?? c.streetName,
      BUILDING_NAME: c.BUILDING_NAME ?? c.buildingName,
      ADDR_LEVEL: c.ADDR_LEVEL ?? c.addrLevel,
      ADDR_UNIT: c.ADDR_UNIT ?? c.addrUnit,
      POSTAL_CODE: c.POSTAL_CODE ?? c.postalCode,
      FOREIGN_ADDRESS_LINE_1: c.FOREIGN_ADDRESS_LINE_1 ?? c.foreignAddressLine1,
      FOREIGN_ADDRESS_LINE_2: c.FOREIGN_ADDRESS_LINE_2 ?? c.foreignAddressLine2,
      primaryAddressParts: c.primaryAddressParts,
      NATIONALITY_CD: c.NATIONALITY_CD ?? c.nationalityCd,
      nationality: c.NATIONALITY_CD ?? c.nationalityCd ?? c.nationality,
      nationalityName: c.nationalityName ?? c.nationalityText ?? c.NATIONALITY_NAME,
      aliases: c.aliases ?? c.alias ?? c.otherName ?? c.OTHER_NAME ?? c.aliasName,
    };
  } catch (err) {
    console.warn('[SingbacPost] nominator contact enrich failed:', err);
    return null;
  }
}

/**
 * 노미니 이사의 nominator → Notice by Nominee Director 의 개인 지명자(client_*) person 객체.
 *   L_NOMINATOR(싱크·엔티티 입력, bizfile 원본 필드) 우선 — /entity/:id/nominator 가 예전 데이터(컨택트 코드)도 같은 형식으로 돌려준다.
 *   Notice 는 개인 지명자 서식이라 법인 지명자면 채우지 않는다(null). 조회 실패 시에만 예전처럼 컨택트로 채움.
 */
async function loadIndividualNominatorPerson(entityCd: string, nominatorCd: string): Promise<Record<string, any> | null> {
  const ncd = String(nominatorCd || '').trim();
  if (!entityCd || !ncd) return null;
  try {
    const res: any = await entityApi.getEntityNominator(entityCd, ncd, 'director');
    const payload: any = res?.data ?? null;
    const data: any = payload?.nominator ? payload : (payload?.data ?? null);
    const n: any = data?.nominator;
    if (n && typeof n === 'object') {
      if (!isIndividualNominator(n)) return null;
      const display: any = data?.display || {};
      return {
        name: String(n.Name || '').trim(),
        aliases: String(n.NameAlias || '').trim(),
        identificationNo: String(n.IdentificationNo || '').trim(),
        nationalityName: String(display.Nationality || n.Nationality || '').trim(),
        dateOfBirth: String(n.BirthDate || '').trim(),
        residentialAddress: formatNominatorAddress(n),
        email: String(n.EmailAddress || '').trim(),
      };
    }
  } catch (err) {
    console.warn('[SingbacPost] nominator detail failed — contact fallback:', err);
  }
  return enrichContact(ncd);
}

// POST — 설립 후 작업이므로 이미 등록된 회사(ENTITY)를 고르면, 공통 SG Incorporation (Post)
//   preset 과 동일한 표준 엔드포인트로 회사 프로필 + 이사/비서/멤버를 불러와 문서를 자동 시드한다.
//   · 회사 힌트    : /api/startTask/common/entity/{cd}
//   · 프로필/회계  : docGenApi.getEntityProfile (registered address, incorporation date, FYE)
//   · 이사/비서/멤버: getActiveDirectors / getActiveSecretaries / getActiveMembers (IS_DEL=0)
//   (설립 진행 데이터 /api/aiDocuments/incorporation/entity-data 는 쓰지 않는다.)
async function autofillFromIncorporation(entityCode: string) {
  const cd = String(entityCode || '').trim();
  if (!cd || cd === lastAutofillIcp.value) return;
  if (autofilledKeys.value.size || lastAutofillIcp.value) {
    for (const k of autofilledKeys.value) fill.setValue(k, '');
    autofilledKeys.value = new Set();
  }
  lastAutofillIcp.value = cd;
  try {
    // 회사 힌트(빠른 회사 필드) — 실패해도 프로필로 보완
    let hint: Record<string, any> | null = null;
    try {
      const res = await fetch(`/api/startTask/common/entity/${encodeURIComponent(cd)}`, {
        credentials: 'include',
      });
      const json: any = await res.json();
      const e = json?.data?.entity || json?.data || json?.entity || null;
      if (e) {
        hint = {
          ...e,
          registeredAddress:
            e.registeredAddress || e.registeredOfficeAddress || buildEntityAddress(e) || undefined,
        };
      }
    } catch {
      /* 프로필로 보완 */
    }

    const [profileRes, dirRes, secRes, memRes, offRes] = await Promise.all([
      docGenApi.getEntityProfile(cd, 'SG').catch(() => null),
      getActiveDirectors(cd).catch(() => null),
      getActiveSecretaries(cd).catch(() => null),
      getActiveMembers(cd).catch(() => null),
      entityApi.getEntityOfficer(cd).catch(() => null),
    ]);

    const mapped = mapSingbacEntityToPostValues({
      profile: (profileRes as any)?.data ?? profileRes,
      entityHint: hint,
      directors: (dirRes as any)?.data?.directors || [],
      secretaries: (secRes as any)?.data?.secretaries || [],
      members: (memRes as any)?.data?.members || [],
    });

    const written = new Set<string>();
    for (const [k, v] of Object.entries(mapped.values)) {
      const s = String(v ?? '').trim();
      // 빈 칸만 시드 (사용자 입력 미덮어씀)
      if (s && !String(fill.values[k] || '').trim()) {
        fill.setValue(k, s);
        written.add(k);
      }
    }
    autofilledKeys.value = written;
    if (restoredFromSaved.value) { emitProgress(); return; } // Edit — 저장된 부수·인물값 유지

    // 노미니 디렉터(IS_NOMINEE=1) 판정 — officer register 우선(플래그·NOMINEE_DIRCTOR_CD·contactCode 보유).
    const officers = extractOfficers(offRes);
    const nomineeOfficers = officers.filter(isNomineeOfficer);
    hasNomineeDirector.value = nomineeOfficers.length > 0 || mapped.local.hasNominee;
    nomineeName.value =
      String(nomineeOfficers[0]?.fullName || nomineeOfficers[0]?.name || nomineeOfficers[0]?.contactName || '').trim() ||
      mapped.local.nomineeName || '';

    // 최상위 nominator/resident 잔여값 정리(이제 nom_{id}/ 스코프로 채움).
    for (const k of [...NOMINATOR_KEYS, ...RESIDENT_DIRECTOR_KEYS]) fill.setValue(k, '');

    // 노미니 명수대로 — 노미니별 nom_{id}/ 스코프에 본인 신원(resident_director_*) · 그 노미니의 nominator(client_*) · 등재일.
    const nomineeIds: number[] = [];
    for (let i = 0; i < nomineeOfficers.length; i += 1) {
      const nomId = i + 1;
      nomineeIds.push(nomId);
      const nom: any = nomineeOfficers[i];
      const sc = `nom_${nomId}/`;
      // Nominee Director 본인 신원(Indemnity·Notice 의 resident director) = 노미니 이사 자신의 contact
      const residentCd = String(nom?.contactCode || nom?.contactCd || nom?.CONTACT_CD || '').trim();
      if (residentCd) {
        const rd = await enrichContact(residentCd);
        if (rd) await fill.applyPersonToKey(`${sc}resident_director_name`, rd);
      } else if (nom?.fullName || nom?.name) {
        fill.setValue(`${sc}resident_director_name`, String(nom.fullName || nom.name));
      }
      // Notice 의 nominator(개인 지명자) = 그 노미니의 NOMINEE_DIRCTOR_CD contact → client_*
      const nominatorCd = nomineeCodeOf(nom);
      if (nominatorCd) {
        const person = await loadIndividualNominatorPerson(cd, nominatorCd);
        if (person) await fill.applyPersonToKey(`${sc}client_name`, person);
      }
      // "Date on which I became individual nominator's nominee" = 노미니 등재(선임)일
      const apptDate = String(
        nom?.appointmentDate || nom?.date || nom?.APPOINTMENT_DATE || nom?.dateOfAppointed || '',
      ).trim();
      if (apptDate) {
        const iso = dateToHtmlInput(apptDate);
        fill.setValue(`${sc}date_became_nominee`, iso ? iso.replace(/-/g, '/') : apptDate);
      }
    }
    if (!nomineeIds.length) nomineeIds.push(1);

    // 대표 nominator → 최상위 client_*(Structure Chart 의 company-level Client).
    const primaryNominatorCd = nomineeOfficers[0] ? nomineeCodeOf(nomineeOfficers[0]) : '';
    if (primaryNominatorCd) {
      const person = await loadIndividualNominatorPerson(cd, primaryNominatorCd);
      if (person) await fill.applyPersonToKey('client_name', person);
    }

    // 부수 인원 반영 — NON NOMINEE/Disclosure/Undertaking(client-director) + Notice/Indemnity(nominee).
    const ids = mapped.local.directorIds;

    // 각 client-director 신원 — 그 이사 contact 로 dir_{id}/client_*(이름·주소·NRIC·국적·DOB) 채움.
    const dirRows: any[] = (dirRes as any)?.data?.directors || [];
    for (let i = 0; i < ids.length; i += 1) {
      const d: any = dirRows[i];
      const cc = String(d?.contactCd || d?.CONTACT_CD || d?.contactCode || '').trim();
      if (!cc) continue;
      const person = await enrichContact(cc);
      if (person) await fill.applyPersonToKey(`dir_${ids[i]}/client_name`, person);
    }

    // Undertaking(dir 스코프)이 참조하는 resident director = 대표 노미니 → 각 dir 부수에 세팅.
    const primaryResidentName = String(fill.values['nom_1/resident_director_name'] || nomineeName.value || '').trim();
    if (primaryResidentName) {
      for (const id of ids) fill.setValue(`dir_${id}/resident_director_name`, primaryResidentName);
    }
    await nextTick();
    docRef.value?.restoreLocalState?.({ directorIds: ids, nomineeIds });

    await nextTick();
    emitProgress();
  } catch (err) {
    console.warn('[SingbacPost] entity autofill failed:', err);
    lastAutofillIcp.value = '';
  }
}

watch(
  () => props.entityData,
  (ed) => seedCompanyFromEntityData(ed),
  { immediate: true, deep: true },
);

watch(
  entityCd,
  (cd, prev) => {
    if (cd) {
      if (prev && prev !== cd) { lastAutofillIcp.value = ''; restoredFromSaved.value = false; }
      void autofillFromIncorporation(cd);
    }
  },
  { immediate: true },
);

onMounted(async () => {
  try {
    await loadCmBaseCodeOptions('B04');
    for (const [k, v] of Object.entries(fill.values)) {
      if (!/nationality/i.test(k)) continue;
      const raw = String(v || '').trim();
      if (!/^B\d{5}$/i.test(raw)) continue;
      const label = nationalityLabelForOutput(raw);
      if (label && label !== raw) fill.setValue(k, label);
    }
  } catch {
    /* ignore */
  }
  try {
    const cfg = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    const steps = cfg?.processSteps;
    taskConfigSteps.value = Array.isArray(steps) ? steps : [];
  } catch {
    /* ignore */
  }
});

function gatherState() {
  return {
    values: { ...fill.exportPayload() },
    local: docRef.value?.gatherLocalState?.() || null,
    signers: signersCtx.exportAll(),
    entityCd: entityCd.value,
    taskTitle: effectiveTitle.value,
    hasNomineeDirector: hasNomineeDirector.value,
    nomineeName: nomineeName.value,
  };
}

function restoreState(s: any) {
  if (!s) return;
  restoredFromSaved.value = true;
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) {
      if (v != null) fill.setValue(k, String(v));
    }
  }
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) docRef.value?.restoreLocalState?.(s.local);
  if (typeof s.hasNomineeDirector === 'boolean') hasNomineeDirector.value = s.hasNomineeDirector;
  if (s.nomineeName != null) nomineeName.value = String(s.nomineeName);
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) {
    progress.value = 'Please enter a Task Title.';
    return;
  }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || '').trim();
    const valuesSnap = fill.exportPayload();
    signersCtx.seedFromValuesIfEmpty(valuesSnap);
    const baseFormRaw: Record<string, any> = { ...valuesSnap };
    for (const [k, v] of Object.entries(valuesSnap)) {
      if (k.includes('/') && v) {
        const bare = bareKey(k);
        if (!baseFormRaw[bare]) baseFormRaw[bare] = v;
      }
    }
    const signersMap = signersCtx.exportAll();
    const baseForm = normalizeSingbacPreFormDataForWord(baseFormRaw);
    const fallbackSigners = buildSgPreSignersFromValues(valuesSnap);

    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      icpCd: entityCd.value || null,
      presetForm: gatherState(),
      docType: 'SINGBAC_INCORPORATION_POST',
      isMyPreset: props.selection?.isMyPreset === true,
      formData: baseForm,
      signers: signersMap,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) {
        progress.value = 'Error: invalid edit task id';
        creating.value = false;
        return;
      }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'Incorporation Post',
          taskTitle: effectiveTitle.value,
          taskFlow,
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false;
        return;
      }
    }

    progress.value = 'Preparing documents…';
    // Edit 재진입 시 selection.documents 가 생성된 부수 문서(같은 tmpId 다수)일 수 있음 → tmpId 기준 dedupe.
    const rawDocs = props.selection.documents || [];
    const seenTmpl = new Set<number>();
    const docs = rawDocs.filter((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return true;
      if (seenTmpl.has(tid)) return false;
      seenTmpl.add(tid);
      return true;
    });
    const local: any = docRef.value?.gatherLocalState?.() || { directorIds: [1] };
    const directorIds: number[] = Array.isArray(local.directorIds) && local.directorIds.length
      ? local.directorIds
      : [1];
    // 노미니 부수는 0개 허용(문서 옆 X로 전부 삭제 → 노미니 문서 미생성).
    const nomineeIds: number[] = Array.isArray(local.nomineeIds) ? local.nomineeIds : [];
    // 노미니 전용 문서 생성 여부 = 프리뷰 표시 여부(자동 감지 OR 수동 추가). 미표시면 생성 제외.
    const includeIndemnity = local.includeIndemnity ?? hasNomineeDirector.value;
    const includeNominee = local.includeNominee ?? hasNomineeDirector.value;
    const companyLabel = String(baseForm.company_name || 'Company');

    type DocSave = {
      templateId: number;
      label: string;
      personName: string;
      formData: Record<string, any>;
      signers?: any[];
    };
    const docsToSave: DocSave[] = [];

    type SbKind =
      | 'driw'
      | 'indemnity'
      | 'nonnom'
      | 'nominee_notice'
      | 'undertaking'
      | 'disclosure'
      | 'chart'
      | 'other';
    function classifySingbacDoc(name: string): SbKind {
      const n = String(name || '').toLowerCase();
      // NON NOMINEE 는 'nominee' 보다 먼저 판정한다.
      if (/non[\s_-]*nominee/.test(n)) return 'nonnom';
      if (/disclosure|declaration|interest|s\.?\s*165|s\.?\s*156/.test(n)) return 'disclosure';
      if (/driw|first\s*board|resolution/.test(n)) return 'driw';
      if (/indemnity|service\s*(&|and)?\s*indemnity/.test(n)) return 'indemnity';
      if (/nominee/.test(n)) return 'nominee_notice';
      if (/undertaking/.test(n)) return 'undertaking';
      if (/structure|chart/.test(n)) return 'chart';
      return 'other';
    }

    /**
     * per-director 문서(NON NOMINEE / Disclosure)에서 그 이사(scope dir_{id}/) 것만 써야 하는 키.
     * 최상위 client_* 값(Undertaking 등에서 입력된 대표 client)이 모든 부수에 새지 않도록,
     * 스코프 문서 생성 시 최상위 client_* 를 먼저 차단하고 스코프 값만 인정한다.
     */
    const SCOPE_OWNED_KEYS: Array<{ re: RegExp; keys: string[] }> = [
      {
        re: /^dir_\d+\/$/,
        keys: [
          'client_name', 'client_nric', 'client_address', 'client_nationality',
          'client_dob', 'client_aliases',
          // Undertaking 이 참조하는 resident director(대표 노미니) — 부수별 값만.
          'resident_director_name',
          // 부수별 날짜 — 이사별로 다른 날짜 허용(NON NOMINEE/Disclosure/Undertaking).
          'undertaking_date', 'notice_date', 'disclosure_date',
        ],
      },
      {
        // 노미니별 부수 — 본인 신원(resident_director_*) · 그 노미니의 nominator(client_*) · 등재일 · 날짜.
        re: /^nom_\d+\/$/,
        keys: [
          'resident_director_name', 'resident_director_nric', 'resident_director_address',
          'resident_director_nationality', 'resident_director_dob', 'resident_director_aliases',
          'client_name', 'client_nric', 'client_address', 'client_nationality',
          'client_dob', 'client_aliases', 'date_became_nominee',
          // 노미니별 날짜 — 노미니별로 다른 날짜 허용(Notice by Nominee / ND Indemnity).
          'notice_date', 'agreement_date',
        ],
      },
    ];

    function formDataForScope(scopePrefix: string) {
      const out: Record<string, any> = {};
      const owned = SCOPE_OWNED_KEYS.find((r) => r.re.test(scopePrefix))?.keys;
      for (const [k, v] of Object.entries(baseForm)) {
        if (String(k).includes('/')) continue;
        if (owned && owned.includes(String(k))) continue;
        out[k] = v;
      }
      for (const [k, v] of Object.entries(baseForm)) {
        if (!String(k).startsWith(scopePrefix)) continue;
        const bare = String(k).slice(scopePrefix.length);
        if (bare) out[bare] = v;
        out[k] = v;
      }
      return normalizeSingbacPreFormDataForWord(out);
    }

    function signersForKind(kind: SbKind): any[] | undefined {
      if (kind === 'driw') {
        const list = signersMap[SG_PRE_SIGNER_KEYS.driw];
        if (list?.length) return list;
        return fallbackSigners.length ? fallbackSigners : undefined;
      }
      return undefined;
    }

    function pushOnce(
      d: any,
      formData: Record<string, any>,
      personName: string,
      labelSuffix = '',
      signers?: any[],
    ) {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return;
      // 생성 파일명 접미사(" — <사람/역할>") 제거 — 재저장 시 누적 방지. 원본명은 " - "(hyphen)만 쓰므로 안전.
      const baseLabel = String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, '').split(' — ')[0].trim();
      docsToSave.push({
        templateId: tid,
        label: labelSuffix ? `${baseLabel} — ${labelSuffix}` : baseLabel,
        personName,
        formData: normalizeSingbacPreFormDataForWord(formData),
        signers: signers?.length ? signers : undefined,
      });
    }

    function companyForm(): Record<string, any> {
      const companyFd: Record<string, any> = {};
      for (const [k, v] of Object.entries(baseForm)) {
        if (String(k).includes('/')) continue;
        companyFd[k] = v;
      }
      return companyFd;
    }

    for (const d of docs) {
      const kind = classifySingbacDoc((d as any).templateName || (d as any).docCode || '');
      // 노미니 전용 문서 — 프리뷰에서 표시(감지/수동추가)된 것만 생성.
      if (kind === 'indemnity' && !includeIndemnity) continue;
      if (kind === 'nominee_notice' && !includeNominee) continue;
      // NON NOMINEE · Disclosure · Undertaking — client-director 수만큼(부수) 생성(dir_{id}/ 스코프).
      if (kind === 'nonnom' || kind === 'disclosure' || kind === 'undertaking') {
        for (const id of directorIds) {
          const fd = formDataForScope(`dir_${id}/`);
          const person = String(fd.client_name || `Director ${id}`);
          pushOnce(d, fd, person, directorIds.length > 1 ? `Director ${id}` : '');
        }
        continue;
      }
      // Notice by Nominee · ND Indemnity — 노미니 이사 수만큼(부수) 생성(nom_{id}/ 스코프).
      if (kind === 'nominee_notice' || kind === 'indemnity') {
        for (const id of nomineeIds) {
          const fd = formDataForScope(`nom_${id}/`);
          const person = String(fd.resident_director_name || `Nominee ${id}`);
          pushOnce(d, fd, person, nomineeIds.length > 1 ? `Nominee ${id}` : '');
        }
        continue;
      }
      // 회사 단위 문서 — DRIW 만 이사회 [[Signers]] 배정
      pushOnce(d, companyForm(), companyLabel, '', signersForKind(kind));
    }

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            taskId,
            documents: docsToSave,
            replaceExisting: editMode.value,
          }),
        }).then((r) => r.json());
        if (!saveRes?.success) {
          progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
          console.warn('[SingbacPost] prepare-documents failed:', saveRes);
        }
      } catch (err: any) {
        console.warn('[SingbacPost] document save failed:', err);
        progress.value = `⚠ Document save failed: ${err?.message || err}`;
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      /* ignore */
    }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presetTaskId: props.selection.task.PRESET_TASK_ID,
          taskId,
        }),
      });
    } catch {
      /* ignore */
    }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[SingbacPost] create failed', e);
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="singbac-post-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Incorporation Post' }}</div>
        <div class="co-subtitle">Singbac — Incorporation Post · live document package</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change entity</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field
        v-model="titleModel"
        density="compact"
        variant="outlined"
        hide-details
        label="Task Title"
        placeholder="e.g. Singbac Incorporation Post — NewCo Pte. Ltd."
      />
    </div>

    <div v-if="!studio && entityCd" class="co-entity">
      Linked company: <strong>{{ entityData?.entityName || entityData?.companyName || entityCd }}</strong>
    </div>

    <div class="co-doc">
      <SingbacIncorporationPostPackageDoc ref="docRef" :has-nominee="hasNomineeDirector" />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn
        color="primary"
        class="text-none"
        :loading="creating"
        :disabled="!effectiveTitle || creating"
        @click="createTask"
      >
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.singbac-post-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.singbac-post-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-entity { font-size: 12.5px; color: #42506a; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
